/**
 * BulkFindEspaciosModal — Modal para buscar tiendas via Google Places
 * y crear espacios en bloque vinculados a una cadena.
 *
 * Props:
 *   open, onOpenChange
 *   cadena: { id, nombre } — para pre-rellenar query y vincular
 *   defaultTipo: tipo por defecto al crear ("Perfumería", "Farmacia"...)
 *   onImported: callback tras importar (para refrescar lista)
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Loader2, MapPin, Phone, Star, Sparkles, AlertCircle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REGIONS = [
  { v: "es", label: "🇪🇸 España" },
  { v: "pt", label: "🇵🇹 Portugal" },
  { v: "ad", label: "🇦🇩 Andorra" },
  { v: "fr", label: "🇫🇷 Francia" },
];

const TIPOS = [
  "Perfumería", "Farmacia", "Tienda", "Centro Comercial",
  "Supermercado", "Restaurante", "Hotel", "Clínica", "Otro",
];

export default function BulkFindEspaciosModal({ open, onOpenChange, cadena, defaultTipo = "Perfumería", onImported }) {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("es");
  const [tipo, setTipo] = useState(defaultTipo);
  const [results, setResults] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState({ configured: null });

  useEffect(() => {
    if (open) {
      api.get("/places/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false }));
      if (cadena?.nombre && !query) {
        // Pre-rellenar solo con el nombre de la cadena (sin "España"/"Portugal":
        // Google funciona MUCHO mejor con marca+ciudad concreta o marca a secas
        // que con marca+país genérico).
        setQuery(cadena.nombre);
      }
    } else {
      // Reset al cerrar
      setResults([]);
      setSelected(new Set());
      setNextToken(null);
      setHasSearched(false);
    }
    // eslint-disable-next-line
  }, [open, cadena]);

  const [hasSearched, setHasSearched] = useState(false);

  const buscar = async (token = null) => {
    if (query.trim().length < 2) {
      toast.error("Introduce al menos 2 caracteres");
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const { data } = await api.post("/places/search", {
        query, region_code: region, page_token: token,
      });
      if (token) {
        setResults((prev) => [...prev, ...data.items]);
      } else {
        setResults(data.items);
        setSelected(new Set());
      }
      setNextToken(data.next_page_token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al buscar");
    } finally {
      setLoading(false);
    }
  };

  // Sugerencias de búsqueda contextual cuando 0 resultados:
  // Google funciona mucho mejor con marca + ciudad concreta
  const sugerencias = React.useMemo(() => {
    // Extraer la marca del query actual (primer token significativo)
    const q = (query || "").trim();
    let marca = cadena?.nombre || "";
    if (q) {
      // Quitar palabras genéricas (España, Portugal, Spain...)
      const noisy = /\b(españa|portugal|spain|portu|andorra|francia|france)\b/gi;
      marca = q.replace(noisy, "").trim() || cadena?.nombre || q;
    }
    if (!marca) return [];
    const provincias = ["Sevilla", "Málaga", "Madrid", "Barcelona", "Valencia", "Granada", "Cádiz", "Córdoba"];
    const lisboa = ["Lisboa", "Porto", "Coimbra", "Faro"];
    const base = region === "pt" ? lisboa : provincias;
    return base.slice(0, 6).map((p) => `${marca} ${p}`);
  }, [cadena, region, query]);

  const toggle = (pid) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(pid) ? n.delete(pid) : n.add(pid);
      return n;
    });
  };

  const seleccionarTodo = () => {
    if (selected.size === results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.map((r) => r.place_id)));
    }
  };

  const importar = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/places/import", {
        place_ids: Array.from(selected),
        cadena_id: cadena?.id,
        tipo,
      });
      toast.success(`${data.inserted} espacios creados · ${data.skipped_duplicates} duplicados omitidos`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} errores al importar (consola)`);
        console.warn("Place import errors:", data.errors);
      }
      onImported && onImported();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Buscar tiendas con Google
            {cadena && <span className="text-xs text-zinc-500 font-normal">· cadena {cadena.nombre}</span>}
          </DialogTitle>
        </DialogHeader>

        {status.configured === false && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-300">Google Places no está configurado</div>
              <div className="text-xs text-amber-200/70 mt-1">
                Configura la variable de entorno <code className="px-1 bg-zinc-800 rounded">GOOGLE_PLACES_API_KEY</code> en el backend.
                Sigue las instrucciones que te pasó tu agente o ve a Google Cloud Console → Places API (New).
              </div>
            </div>
          </div>
        )}

        {/* Buscador */}
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-5">
            <Label className="text-xs text-zinc-500">Búsqueda</Label>
            <Input
              data-testid="bulk-find-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && buscar()}
              placeholder='ej. "Primor Andalucia" o "Farmacia Sevilla"'
              className="bg-zinc-900 border-zinc-800"
            />
          </div>
          <div className="col-span-3">
            <Label className="text-xs text-zinc-500">Región</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {REGIONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-500">Tipo a crear</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 flex items-end">
            <Button
              onClick={() => buscar()}
              disabled={loading || !status.configured}
              data-testid="bulk-find-search-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="text-[11px] text-zinc-500 -mt-1">
          💡 Combina <strong className="text-zinc-300">marca + ciudad/provincia</strong> (ej. <em>"Primor Málaga"</em>) para mejores resultados.
        </div>

        {/* Selección global */}
        {results.length > 0 && (
          <div className="flex items-center gap-3 text-xs text-zinc-500 -mb-1">
            <button onClick={seleccionarTodo}
              className="text-amber-400 hover:underline" data-testid="bulk-find-select-all">
              {selected.size === results.length ? "Deseleccionar todo" : "Seleccionar todo"}
            </button>
            <span>·</span>
            <span>{selected.size} de {results.length} seleccionados</span>
          </div>
        )}

        {/* Resultados */}
        <div className="overflow-y-auto flex-1 space-y-1" data-testid="bulk-find-results">
          {results.map((r) => {
            const checked = selected.has(r.place_id);
            return (
              <label key={r.place_id}
                data-testid={`bulk-find-item-${r.place_id}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                  ${checked ? "bg-amber-500/10 border-amber-500/30" : "bg-zinc-900/40 border-zinc-800 hover:bg-zinc-900/80"}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggle(r.place_id)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-100 truncate flex items-center gap-2">
                    {r.name}
                    {r.status === "CLOSED_PERMANENTLY" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">CERRADO</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5 truncate">
                    <MapPin className="w-3 h-3 shrink-0" /> {r.address || "—"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                    {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                    {r.rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        {r.rating} ({r.rating_count || 0})
                      </span>
                    )}
                    {r.lat && r.lon && (
                      <span className="font-mono text-zinc-600">{r.lat.toFixed(4)}, {r.lon.toFixed(4)}</span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
          {!loading && results.length === 0 && !hasSearched && (
            <div className="text-center py-12 text-sm text-zinc-500">
              Pulsa <strong>Buscar</strong> para descubrir tiendas
            </div>
          )}
          {!loading && results.length === 0 && hasSearched && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-5 my-2" data-testid="bulk-find-no-results-help">
              <div className="text-sm text-amber-300 font-semibold mb-2">
                🔍 Sin resultados para "{query}"
              </div>
              <div className="text-xs text-zinc-400 leading-relaxed mb-3">
                Google Places funciona mejor con <strong className="text-zinc-300">marca + ciudad/provincia concreta</strong> en lugar de "marca + país".
                Prueba con uno de estos:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sugerencias.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setQuery(s); setTimeout(() => buscar(), 50); }}
                    data-testid={`bulk-find-suggestion-${s.replace(/\s/g, "-")}`}
                    className="px-2.5 py-1 text-xs rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-zinc-500 mt-3 italic">
                💡 Tip: si tu cadena tiene un nombre genérico ("Primor", "Aromas"…) Google la confunde con palabras comunes. Añade siempre una ciudad/provincia.
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        {nextToken && (
          <Button variant="outline" onClick={() => buscar(nextToken)} disabled={loading}
            data-testid="bulk-find-load-more" className="bg-transparent border-zinc-700 text-zinc-300 self-center">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cargar siguiente página"}
          </Button>
        )}

        <DialogFooter className="border-t border-zinc-800 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button
            onClick={importar}
            disabled={importing || selected.size === 0}
            data-testid="bulk-find-import-btn"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Crear {selected.size} espacio{selected.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
